import React, { useState, useEffect } from 'react';
import { Form, TextInput, ReferenceInput, AutocompleteInput, NumberField, useTranslate, useGetIdentity, useLocaleState } from 'react-admin';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer } from 'recharts';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import moment from 'moment';

import { fetchTimeEntriesStats, fetchUserStats } from '../../../../services/api';
import { countWorkingDays } from '../../../../services/utils';

const optionText = choice => choice?.name ? choice.name : '';

const labelSx = {
    display: 'inline-flex',
    minWidth: '230px'
};

const chartBoxSx = {
    height: '300px',
    width: '100%',
    flex: 1,
    position: 'relative',
    '& tspan': {
        fontSize: '11px',
        textTransform: 'capitalize'
    }
};

const typeListSx = {
    '& li': {
        textTransform: 'capitalize'
    }
};

const Leaderboard = () => {
    const defaultMonth = moment().format('YYYY-MM');
    const [month, setMonth] = useState(defaultMonth);
    const [typeId, setTypeId] = useState();
    const [stats, setStats] = useState();
    const [typeStats, setTypeStats] = useState();
    const [globalUserStats, setGlobalUserStats] = useState();
    const [globalUserTypesStats, setGlobalUserTypesStats] = useState();
    const [globalUserMostUsedTypesStats, setGlobalUserMostUsedTypesStats] = useState();
    const [monthUserMostUsedTypesStats, setMonthUserMostUsedTypesStats] = useState();
    const [workingDaysCount, setWorkingDaysCount] = useState();
    const { data: account } = useGetIdentity();
    const translate = useTranslate();
    const [locale] = useLocaleState();

    const monthUserStats = stats?.data?.find(item => item?.group_by_id === account?.id);

    const filterTimeEntriesByTypes = (items) => {
        if (!items) {
            return null;
        }

        const customOrder = ['development', 'analysis', 'r&d', 'support', 'maintenance', 'testing', 'communication', 'meeting', 'management', 'office', 'prospection', 'design', 'training', 'veille', 'other', 'documentation'];
        customOrder.forEach(key => {
            if (!(key in items)) {
                items[key] = 0;
            }
        });

        const filteredTypesStats = Object.entries(items)?.sort((a, b) => {
            const indexA = customOrder.indexOf(a[0]);
            const indexB = customOrder.indexOf(b[0]);
            return indexA - indexB;
        });
        return filteredTypesStats;
    };

    useEffect(() => {
        const fetchStats = async () => {
            const response = await fetchUserStats(account?.id);
            setGlobalUserStats(response);

            if (response) {
                let filteredTypesStats = Object.entries(response?.time_entries?.types)?.filter(([, value]) => value > 0)?.sort((a, b) => b[1] - a[1]);
                setGlobalUserMostUsedTypesStats(filteredTypesStats);

                filteredTypesStats = filterTimeEntriesByTypes(response?.time_entries?.types);
                setGlobalUserTypesStats(filteredTypesStats);
            }
        };

        fetchStats();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const fetchStats = async () => {
            const momentMonth = month ? moment(`${month}-01`) : moment();
            const startDate = momentMonth.startOf('month').format('YYYY-MM-DD');
            const endDate = momentMonth.endOf('month').format('YYYY-MM-DD');

            const filters = {
                start_date: startDate,
                end_date: endDate,
                freq: 'monthly',
                group_by: 'user'
            };
            const newStats = await fetchTimeEntriesStats(filters);
            let data;

            if (newStats?.data?.length > 0) {
                const promises = newStats.data.map(async item => {
                    const response = await fetchTimeEntriesStats({ ...filters, user_id: item?.group_by_id, group_by: 'type' });
                    const types = response?.data?.sort((a, b) => b.value - a.value);
                    return { ...item, types };
                });
                data = await Promise.all(promises);
            }
            data = data?.sort((a, b) => b.value - a.value);
            setStats({ ...newStats, data });
            setTypeStats({ ...newStats, data });

            const newWorkingDaysCount = countWorkingDays(startDate, endDate)
            setWorkingDaysCount(newWorkingDaysCount);
            // TODO: remove leaves from working days?
        }

        fetchStats();
    }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const types = monthUserStats?.types?.reduce((acc, item) => ({ ...acc, [item.name?.toLowerCase()]: item.value }), {});
        const filteredTypesStats = filterTimeEntriesByTypes(types);
        setMonthUserMostUsedTypesStats(filteredTypesStats);
    }, [monthUserStats]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const fetchStats = async () => {
            const momentMonth = month ? moment(`${month}-01`) : moment();
            const startDate = momentMonth.startOf('month').format('YYYY-MM-DD');
            const endDate = momentMonth.endOf('month').format('YYYY-MM-DD');

            const filters = {
                start_date: startDate,
                end_date: endDate,
                freq: 'monthly',
                group_by: 'user',
                type_id: typeId
            };
            let newStats = await fetchTimeEntriesStats(filters);
            newStats.data = newStats.data?.sort((a, b) => b.value - a.value);
            setTypeStats(newStats);
        };

        if (typeId) {
            fetchStats();
        }
        else {
            setTypeStats(stats);
        }
    }, [typeId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChangeMonth = (e) => {
        setMonth(e?.target?.value);
    };

    const handleChangeType = (id) => {
        setTypeId(id || null);
    };

    return (
        <Box mt={2}>
            <Typography variant="h6">{translate('Personal')}</Typography>
            <Box mb={5}>
                <Box sx={{ display: 'flex' }}>
                    <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', '& ol': { marginTop: 0, fontSize: '0.875rem' } }}>
                        <Typography variant="overline">Global</Typography>
                        <Box>
                            <Box sx={{ display: 'flex' }}>
                                <Typography variant="caption" color="textSecondary" sx={labelSx}>{translate('Project count')}</Typography>
                                <NumberField record={globalUserStats} source="projects" />
                            </Box>
                            <Box sx={{ display: 'flex' }}>
                                <Typography variant="caption" color="textSecondary" sx={labelSx}>{translate('Days filled in timesheet')}</Typography>
                                <NumberField record={globalUserStats?.time_entries} source="days" />
                            </Box>
                            {globalUserMostUsedTypesStats?.length > 0 && (
                                <Box sx={typeListSx}>
                                    <Typography variant="caption" color="textSecondary">{translate('Most commonly used types in timesheet')}</Typography>
                                    <ol>
                                        {globalUserMostUsedTypesStats?.map(([key, value], index) => (
                                            <li key={index}>{key} (<NumberField record={{ value }} source="value" />)</li>
                                        ))}
                                    </ol>
                                </Box>
                            )}
                        </Box>
                    </Box>
                    <Box sx={chartBoxSx}>
                        <ResponsiveContainer width="100%" height="100%">
                            <RadarChart cx="50%" cy="50%" outerRadius="80%" data={globalUserTypesStats ? globalUserTypesStats : []}>
                                <PolarGrid />
                                <PolarAngleAxis dataKey={(item) => item[0]} />
                                <Radar dataKey={(item) => item[1]} label={(item) => item[1]?.toUpperCase()} stroke={account?.color || '#8884d8'} fill={account?.color || '#8884d8'} fillOpacity={0.6} />
                            </RadarChart>
                        </ResponsiveContainer>
                    </Box>
                </Box>
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="overline">{new Date(moment(`${month}-01`)).toLocaleString(locale, { month: 'long' })}</Typography>
                        <Form defaultValues={{ month: defaultMonth }}>
                            <TextInput label={false} source="month" type="month" onChange={handleChangeMonth} value={month} helperText={false} />
                        </Form>
                    </Box>
                    <Box>
                        {monthUserStats ? (
                            <Box display="flex">
                                <Box sx={{ flex: 1 }}>
                                    <Box>
                                        <Box sx={{ display: 'flex' }}>
                                            <Typography variant="caption" color="textSecondary" sx={labelSx}>{translate('Hours')} ({translate('total hours/max capacity')})</Typography>
                                            <Box sx={{ display: 'inline-flex', fontSize: '0.875rem' }}>
                                                <NumberField record={{ hours: monthUserStats?.value }} source="hours" options={{ style: 'unit', unit: 'hour' }} />
                                                /
                                                <NumberField record={{ total: workingDaysCount * 8 }} source="total" options={{ style: 'unit', unit: 'hour' }} />
                                                &nbsp;(<NumberField record={{ percent: (monthUserStats?.value / (workingDaysCount * 8)) * 100 }} source="percent" options={{ style: 'unit', unit: 'percent', maximumFractionDigits: 1 }} />)
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Typography variant="caption" color="textSecondary">{translate('Most commonly used types')}</Typography>
                                    <ol>
                                        {monthUserStats?.types?.map((item, index) => (
                                            <li key={index}>{item?.name} (<NumberField record={{ value: item?.value }} source="value" options={{ style: 'unit', unit: 'hour' }} />)</li>
                                        ))}
                                    </ol>
                                </Box>
                                {monthUserMostUsedTypesStats && (
                                    <Box sx={chartBoxSx}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <RadarChart cx="50%" cy="50%" outerRadius="80%" data={monthUserMostUsedTypesStats ? monthUserMostUsedTypesStats : []}>
                                                <PolarGrid />
                                                <PolarAngleAxis dataKey={(item) => item[0]} />
                                                <Radar dataKey={(item) => item[1]} label={(item) => item[1]?.toUpperCase()} stroke={account?.color || '#8884d8'} fill={account?.color || '#8884d8'} fillOpacity={0.6} />
                                            </RadarChart>
                                        </ResponsiveContainer>
                                    </Box>
                                )}
                            </Box>
                        ) : (
                            <Typography>{translate('No data for this month')}</Typography>
                        )}
                    </Box>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '15px' }}>
                <Typography variant="h6">{translate('Team')}</Typography>
                <Form>
                    <ReferenceInput source="type_id" reference="time_entry_types" sort={{ field: 'name', order: 'ASC' }}>
                        <AutocompleteInput optionText={optionText} size="small" label="Type" onChange={handleChangeType} helperText={false} />
                    </ReferenceInput>
                </Form>
            </Box>
            <TableContainer>
                <Table sx={{ minWidth: 650 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>{translate('User')}</TableCell>
                            <TableCell align="right">{translate('Hours')} (h)</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {typeStats?.data?.map((user, index) => (
                            <TableRow
                                key={user.name}
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                                <TableCell component="th" scope="row">
                                    {index + 1}. {user.name}
                                </TableCell>
                                <TableCell align="right">
                                    <NumberField record={{ value: user.value }} source="value" />
                                </TableCell>
                                <TableCell align="right">
                                    <NumberField record={{ value: ((user.value / typeStats.total) * 100)?.toFixed(1) }} source="value" options={{ style: 'unit', unit: 'percent' }} />
                                </TableCell>
                            </TableRow>
                        ))}
                        <TableRow>
                            <TableCell />
                            <TableCell align="right">{translate('Total')}: <NumberField record={{ total: typeStats?.total }} source="total" /></TableCell>
                            <TableCell />
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default Leaderboard;